import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { EVENTS_TYPES } from '../../../pagesNext/shared/Constants';
import { IS_ANDROID_APP, IS_MOBILE_APP } from '../../../services/capacitor/Constants';
import { isAuthenticated } from '../../../services/AccessToken';
import { getLocalStorageItem, setLocalStorageItem } from '../../../services/Helper';
import { historyPush } from '../../../services/History';
import { MAIN_PURPLE_COLOR } from '../../../services/Constants';
import * as sidebarActions from '../../sideBar/Actions';
import * as pushNotificationActions from '../Actions';
import * as notificationsActions from '../../notifications/Actions';
import * as sharedActions from '../../../pagesNext/shared/Actions';

const createDefaultNotificationChannel = async () => {
	const channel = {
		id: 'default_channel',
		name: 'Default Notifications',
		description: 'General app notifications',
		importance: 4,
		visibility: 1,
		vibration: true,
		smallIcon: 'notification_icon',
	};

	try {
		await LocalNotifications.createChannel(channel);
	} catch (error) {
		console.error(`Error creating notification channel '${channel.id}':`, error);
	}
};

const usePushNotifications = () => {
	const dispatch = useDispatch();
	const isLoggedIn = isAuthenticated();

	const handleNotification = useCallback((landingUrl) => {
		if (landingUrl.startsWith('http')) {
			window.open(landingUrl, '_system');
		} else {
			historyPush(landingUrl);
		}
	}, []);

	const handlePushNotifications = useCallback(async () => {
		if (IS_ANDROID_APP) await createDefaultNotificationChannel();
		FirebaseMessaging.addListener('notificationReceived', async ({ notification }) => {
			const { data = {}, body, title } = notification || {};

			const {
				messageType,
				notification_id,
				landing_url,
				send_read_event,
				tap,
				campaign_name,
			} = data;

			await LocalNotifications.schedule({
				notifications: [
					{
						id: notification_id,
						title,
						body: body || '',
						schedule: { at: new Date(Date.now() + 100) },
						channelId: 'default_channel',
						smallIcon: 'notification_icon',
						iconColor: MAIN_PURPLE_COLOR,
					},
				],
			});

			if (messageType === 'notification' && tap) {
				dispatch(notificationsActions.updateNotificationStatusRequest(notification_id));
				handleNotification(landing_url);
				dispatch(sidebarActions.getUseDataRequest());

				if (send_read_event) {
					dispatch(
						sharedActions.sendEventRequest({
							event_name: EVENTS_TYPES.PUSH_NOTIFICATION_READ,
							notification_id,
							landing_url,
							campaign_name,
							body,
						}),
					);
				}
			}
		});

		FirebaseMessaging.requestPermissions().then(async (permission) => {
			if (permission.receive === 'granted') {
				const deviceToken = await FirebaseMessaging.getToken();

				if (deviceToken.token) {
					const storedToken = getLocalStorageItem('deviceToken');
					if (!storedToken || storedToken !== deviceToken.token) {
						setLocalStorageItem('deviceToken', deviceToken.token);
						if (isLoggedIn) {
							dispatch(
								pushNotificationActions.registerPushNotificationForDeviceRequest(
									deviceToken.token,
								),
							);
						}
					}
				}
			}
		});

		FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
			const oldToken = getLocalStorageItem('deviceToken');
			if (oldToken !== token) {
				dispatch(
					pushNotificationActions.deregisterPushNotificationForDeviceRequest(oldToken),
				);
				dispatch(pushNotificationActions.registerPushNotificationForDeviceRequest(token));
				setLocalStorageItem('deviceToken', token);
			}
		});
	}, [dispatch, handleNotification, isLoggedIn]);

	useEffect(() => {
		if (!IS_MOBILE_APP) return;
		handlePushNotifications();
	}, [handlePushNotifications]);
};

export default usePushNotifications;
